import { useData } from '../context/data.context';
import { DeviceSubscription } from '../services/dashboard-service';

export const PLAN_LEVELS: Record<string, number> = {
  Free: 0,
  Basic: 1,
  Advanced: 2,
  Professional: 3,
  Enterprise: 4
};

/** Monthly price per device in USD for self-serve plans. Mirrors the backend DevicePlans. */
export const PLAN_PRICES_USD: Record<string, number> = {
  Basic: 9.99,
  Advanced: 14.99,
  Professional: 29.99
};

const GRACE_PERIOD_DAYS = 10;
const EXPIRING_SOON_DAYS = 10;

/** Maximum days of history allowed for data viewing per plan. Null means unlimited, 0 means no access. */
const PLAN_DATA_DAYS: Record<string, number | null> = {
  Free: 0,
  Basic: 7,
  Advanced: 30,
  Professional: 90,
  Enterprise: null
};

export type PlanStatus = 'none' | 'active' | 'expiring-soon' | 'grace-period' | 'expired';

export type DevicePlan = {
  serialNumber: number;
  deviceName: string;
  /** Plan the device is subscribed to, or null when it has none. */
  subscribedPlan: string | null;
  /** Plan the device currently gets: its subscribed plan, or Free once past the grace period. */
  effectivePlan: string | null;
  planStatus: PlanStatus;
  /** Renewal date, or null when the plan does not lapse (or there is no plan). */
  renewalDate: string | null;
  daysUntilExpiry: number | null;
  graceDaysRemaining: number | null;
};

// eslint-disable-next-line jsdoc/require-jsdoc
function getDaysUntilExpiry(endDate: string): number {
  const end = new Date(endDate).getTime();
  const now = Date.now();
  return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
}

/**
 * Works out where one device stands in its plan's lifecycle.
 * Accounts for a 10-day grace period after the renewal date.
 * @param sub - The device and its active subscription, if any.
 * @returns The device's plan status.
 */
export function getDevicePlan(sub: DeviceSubscription): DevicePlan {
  const base = {
    serialNumber: sub.serialNumber,
    deviceName: sub.deviceName,
    subscribedPlan: sub.planName,
    renewalDate: sub.endDate,
    daysUntilExpiry: null,
    graceDaysRemaining: null
  };
  if (!sub.planName) {
    return { ...base, effectivePlan: null, planStatus: 'none' };
  }
  if (!sub.endDate) {
    // No renewal date (e.g. a granted Enterprise plan)
    return { ...base, effectivePlan: sub.planName, planStatus: 'active' };
  }
  const days = getDaysUntilExpiry(sub.endDate);
  if (days > EXPIRING_SOON_DAYS) {
    return { ...base, effectivePlan: sub.planName, planStatus: 'active', daysUntilExpiry: days };
  }
  if (days > 0) {
    // Close to the renewal date — warn but still active
    return { ...base, effectivePlan: sub.planName, planStatus: 'expiring-soon', daysUntilExpiry: days };
  }
  if (days > -GRACE_PERIOD_DAYS) {
    // Past the renewal date but within the grace period
    return {
      ...base,
      effectivePlan: sub.planName,
      planStatus: 'grace-period',
      daysUntilExpiry: days,
      graceDaysRemaining: GRACE_PERIOD_DAYS + days // days is negative here
    };
  }
  // Past the grace period — treat as Free
  return { ...base, effectivePlan: 'Free', planStatus: 'expired', daysUntilExpiry: days };
}

/**
 * Hook to get the organisation's device plans and check feature access.
 * Plans are bought per device. Organisation-wide features unlock at the highest plan any device
 * holds; data history follows the most restrictive plan, matching the backend, which clamps a
 * multi-device query to the narrowest window among its devices.
 * @returns Device plans, an access checker and the history window.
 */
export default function usePlan(): {
  devicePlans: DevicePlan[];
  devicePlanBySerial: Record<number, DevicePlan>;
  planName: string;
  planLevel: number;
  maxDataDays: number | null;
  /** Plan that sets maxDataDays, for labels like "Basic plan: up to 7 days". */
  dataDaysPlan: string;
  /** Whether any device has a plan; with none the server does not restrict history. */
  hasAnyPlan: boolean;
  hasAccess: (requiredPlan: string) => boolean;
} {
  const { deviceSubscriptions } = useData();
  // The public dashboard renders outside DataProvider, so there are no subscriptions there.
  const devicePlans = (deviceSubscriptions ?? []).map(getDevicePlan);

  const devicePlanBySerial: Record<number, DevicePlan> = {};
  devicePlans.forEach((plan) => {
    devicePlanBySerial[plan.serialNumber] = plan;
  });

  const effectivePlans = devicePlans
    .map((plan) => plan.effectivePlan)
    .filter((name): name is string => !!name);

  // With no device plans at all, keep the long-standing default of Advanced features.
  const planName = effectivePlans.length
    ? effectivePlans.reduce((best, name) =>
        (PLAN_LEVELS[name] ?? 0) > (PLAN_LEVELS[best] ?? 0) ? name : best
      )
    : 'Advanced';
  const planLevel = PLAN_LEVELS[planName] ?? 0;

  // eslint-disable-next-line jsdoc/require-jsdoc
  const hasAccess = (requiredPlan: string): boolean => {
    const required = PLAN_LEVELS[requiredPlan] ?? 0;
    return planLevel >= required;
  };

  // The narrowest window among devices with a plan, as on the server. Unknown plan names are
  // unlimited there too, so only known limited plans count. With no device plans at all, keep the
  // long-standing UI default of the Advanced window.
  const hasAnyPlan = effectivePlans.length > 0;
  let maxDataDays: number | null = hasAnyPlan ? null : PLAN_DATA_DAYS[planName] ?? null;
  let dataDaysPlan = planName;
  effectivePlans.forEach((name) => {
    const days = PLAN_DATA_DAYS[name];
    if (days === undefined || days === null) return;
    if (maxDataDays === null || days < maxDataDays) {
      maxDataDays = days;
      dataDaysPlan = name;
    }
  });

  return {
    devicePlans,
    devicePlanBySerial,
    planName,
    planLevel,
    maxDataDays,
    dataDaysPlan,
    hasAnyPlan,
    hasAccess
  };
}
